/**
 * Error code raised by the backend when a tenant has not licensed the feature a request touches.
 *
 * Lives in `utils/graphql` rather than in the feature folder because it describes the wire
 * contract, and both the Apollo link and the shared error renderer have to agree on it.
 */
export const FEATURE_NOT_LICENSED = "FEATURE_NOT_LICENSED";

/**
 * Returns the denied feature key from a GraphQL error's extensions, or undefined when the error
 * is not an entitlement denial.
 *
 * Two shapes are accepted: the raw wire shape, where graphql-dotnet nests the error's Data
 * dictionary under `extensions.data`, and the shape normalised by the Apollo error link, which
 * lifts the key to `extensions.feature`. Requests that bypass the link — mocked responses in
 * tests, most notably — only ever carry the raw shape, so the fallback is not optional.
 */
export function readUnlicensedFeature(
  extensions: Record<string, unknown> | undefined
): string | undefined {
  if (!extensions || extensions["code"] !== FEATURE_NOT_LICENSED)
    return undefined;

  const normalised = extensions["feature"];
  if (typeof normalised === "string") return normalised;

  const data = extensions["data"] as Record<string, unknown> | undefined;
  const feature = data?.["feature"];

  return typeof feature === "string" ? feature : undefined;
}
